// Removed Typography import as we're now using proper dark mode classes

export function formatCurrency(amount: number) {
  let decimalPlaces = 2;

  if (amount < 0.01) {
    const match = amount.toString().match(/0\.0*([1-9])/);
    if (match && match.index != undefined) {
      decimalPlaces = match.index + match[0].length - 2;
    }
  }

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  }).format(amount);
}

export function isDimmed(condition: boolean) {
  return {
    opacity: condition ? 0.5 : 1,
  };
}

const extractContentText = (part: { [k: string]: unknown }): string => {
  if (typeof part.text === "string") {
    return part.text;
  }
  if (typeof part.content === "string") {
    return part.content;
  }
  if (part.type === "image_url" || part.type === "input_image") {
    return "[image]";
  }
  return JSON.stringify(part, null, 2);
};

const extractImageUrl = (part: { [k: string]: unknown }): string | null => {
  if (typeof part.image_url === "string") {
    return part.image_url;
  }
  if (
    part.image_url &&
    typeof part.image_url === "object" &&
    typeof (part.image_url as { url?: unknown }).url === "string"
  ) {
    return (part.image_url as { url: string }).url;
  }
  if (typeof part.url === "string") {
    return part.url;
  }
  return null;
};

export function getMessageContentText(
  content?:
    | string
    | { [k: string]: unknown }
    | { [k: string]: unknown }[]
    | null
): string {
  if (!content) {
    return "";
  }
  if (typeof content === "string") {
    return content;
  }
  if (Array.isArray(content)) {
    return content
      .map((part) => extractContentText(part))
      .filter(Boolean)
      .join("\n");
  }
  return extractContentText(content);
}

export function getMessageContentParts(
  content?:
    | string
    | { [k: string]: unknown }
    | { [k: string]: unknown }[]
    | null
): { type: "text" | "image"; value: string }[] {
  if (!content) {
    return [];
  }
  if (typeof content === "string") {
    return [{ type: "text", value: content }];
  }
  const parts = Array.isArray(content) ? content : [content];
  const items: { type: "text" | "image"; value: string }[] = [];
  parts.forEach((part) => {
    const imageUrl = extractImageUrl(part);
    if (imageUrl) {
      items.push({ type: "image", value: imageUrl });
      return;
    }
    const text = extractContentText(part);
    if (text) {
      items.push({ type: "text", value: text });
    }
  });
  return items;
}

export function formatTextOrJson(
  text?:
    | string
    | { [k: string]: unknown }
    | { [k: string]: unknown }[]
    | null
): JSX.Element {
  const normalizedText = getMessageContentText(text);
  if (!normalizedText) {
    return <span className="text-foreground"></span>;
  }

  let formattedText = normalizedText;

  // Check if the text contains [ or {
  if (normalizedText.includes("[") || normalizedText.includes("{")) {
    try {
      // Attempt to parse the text as JSON
      const parsedJson = JSON.parse(normalizedText);
      formattedText = JSON.stringify(parsedJson, null, 2);
    } catch (e) {
      // If parsing fails, return the original text
      return <span className="text-foreground">{normalizedText}</span>;
    }

    return (
      <pre className="whitespace-pre-wrap text-foreground">
        <code className="text-foreground">{formattedText}</code>
      </pre>
    );
  } else {
    // If the text doesn't contain [ or {, return it as is
    return <span className="text-foreground">{normalizedText}</span>;
  }
}
